import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import Config from 'react-native-config'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { useNavigation } from '@react-navigation/native'
import { runOnJS } from 'react-native-reanimated'
import { useStyleSheet, StyleService } from '@src/style/service'
import { Debug } from '@config'
import { Icon, Text } from '@components/base'
import { Feature, NutrisenseLogo, ScrollableAvoidKeyboard, useFeatureFlag } from '@components'
import { SignInButton, SignInButtonVariant, SignInForm } from '@screens/SignIn'
import { useSignIn } from '@src/hooks/useSignIn'
import { useAllowSignInWithApple, useAllowSignInWithGoogle } from '@utils/hooks'
import { useSnack } from '@utils/navigatorContext'
import { useGoBack } from '@src/utils'

export const SignIn = () => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation()
  const showSnack = useSnack()
  const allowSignInWithGoogle = useAllowSignInWithGoogle()
  const allowSignInWithApple = useAllowSignInWithApple()
  const { signInWithApple, signInWithGoogle } = useSignIn()
  const goBack = useGoBack()

  const skipProgramSelectionBeforeSignUp = useFeatureFlag(Feature.SkipProgramSelectionBeforeSignUp)

  const onSignInWithGooglePress = async () => {
    try {
      await signInWithGoogle()
    } catch (error: any) {
      showSnack(error.message, null, 'error')
    }
  }

  const onSignInWithApplePress = async () => {
    try {
      await signInWithApple()
    } catch (error: any) {
      showSnack(error.message, null, 'error')
    }
  }

  const onForgotPasswordPress = () => navigation.navigate('PasswordResetRequest')

  const onSignUpPress = () => {
    if (!skipProgramSelectionBeforeSignUp) {
      navigation.navigate('OnboardingSubscriptionOptions')
      return
    }

    navigation.navigate('CreateAccountOptionsStelo')
  }

  const onAdminPress = () => {
    navigation.navigate('ShowAdmin')
  }

  const longPressGesture = Gesture.LongPress()
    .enabled(Debug.useAdminMenu && Config.ENV !== 'production')
    .minDuration(50)
    .onEnd((_, success) => {
      if (success) {
        runOnJS(onAdminPress)()
      }
    })

  const insets = useSafeAreaInsets()

  return (
    <ScrollableAvoidKeyboard
      style={styles.container}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + 12, paddingBottom: insets.bottom + 16 },
      ]}
      keyboardShouldPersistTaps="handled"
    >
      <TouchableOpacity
        onPress={goBack}
        activeOpacity={0.7}
        accessibilityLabel="Go Back"
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
      >
        <Icon name="caret-left" size="20" weight="bold" />
      </TouchableOpacity>
      <View style={styles.headerContainer} testID="SigninScreen/Logo">
        <GestureDetector gesture={longPressGesture}>
          <View>
            <NutrisenseLogo />
          </View>
        </GestureDetector>
      </View>
      <Text type="large" lineSpacing="none" style={styles.welcomeText}>
        Welcome! Let's log you in.
      </Text>
      <SignInForm />
      <TouchableOpacity accessibilityLabel="Forgot your password?" onPress={onForgotPasswordPress}>
        <Text type="large" lineSpacing="none" bold style={styles.forgotPassword}>
          Forgot password?
        </Text>
      </TouchableOpacity>
      <View style={styles.signInButtonContainer}>
        {allowSignInWithGoogle && (
          <SignInButton
            variant={SignInButtonVariant.Google}
            style={styles.signInButton}
            onPress={onSignInWithGooglePress}
          />
        )}
        {allowSignInWithApple && (
          <SignInButton
            variant={SignInButtonVariant.Apple}
            style={styles.signInButton}
            onPress={onSignInWithApplePress}
          />
        )}
        <View style={styles.bottom}>
          <Text type="regular" lineSpacing="none">
            Don't Have An Account?
          </Text>
          <TouchableOpacity
            accessibilityLabel="Register"
            onPress={onSignUpPress}
            testID="SigninScreen/RegisterButton"
          >
            <Text type="regular" lineSpacing="none" bold style={styles.signUp}>
              Register
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScrollableAvoidKeyboard>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 600,
    paddingHorizontal: 16,
  },
  headerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  welcomeText: {
    width: '100%',
    marginBottom: 24,
    textAlign: 'center',
  },
  forgotPassword: {
    paddingVertical: 16,
    textAlign: 'center',
  },
  signInButtonContainer: {
    marginTop: 48,
  },
  signInButton: {
    marginBottom: 16,
  },
  bottom: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 32,
  },
  signUp: {
    marginTop: 16,
    color: 'theme.text.link',
  },
})
